import type {
  CoreDefinition,
  EnemyDefinition,
  EvolutionDefinition,
  ShopDefinition,
  SynergyDefinition,
  UpgradeDefinition,
  UpgradeRarity,
  WaveDefinition,
} from '../game/simulation/types';

export const RARITY_WEIGHTS: Readonly<Record<UpgradeRarity, number>> = {
  common: 56,
  uncommon: 26,
  rare: 12,
  epic: 5,
  legendary: 1,
  mythic: 0,
};

export const CORES: readonly CoreDefinition[] = [
  {
    id: 'aegis',
    name: 'Aegis Core',
    subtitle: 'Fortress protocol',
    rarity: 'common',
    unlockLevel: 1,
    unlockBlueprintId: null,
    color: 0x00e5ff,
    accentColor: 0x80ff24,
    abilityName: 'Aegis Pulse',
    abilityDescription: 'Periodically rebuilds a damage-absorbing energy barrier.',
    abilityCooldown: 9.5,
    ability: { type: 'barrier', power: 145, radius: 132 },
    modifiers: {
      maxHealth: 1.18,
      damage: 0.96,
      attackSpeed: 1,
      armor: 4,
      critChance: 0,
      cooldownReduction: 0,
      area: 1,
    },
  },
  {
    id: 'tempest',
    name: 'Tempest Core',
    subtitle: 'Aggression protocol',
    rarity: 'rare',
    unlockLevel: 2,
    unlockBlueprintId: null,
    color: 0x41f4ff,
    accentColor: 0xffd321,
    abilityName: 'Storm Lance',
    abilityDescription: 'Fires a high-voltage chain through priority targets.',
    abilityCooldown: 8.5,
    ability: { type: 'chain', power: 46, radius: 250, targets: 5 },
    modifiers: {
      maxHealth: 0.94,
      damage: 1.08,
      attackSpeed: 1.14,
      armor: 0,
      critChance: 0.04,
      cooldownReduction: 0,
      area: 1,
    },
  },
  {
    id: 'cryon',
    name: 'Cryon Core',
    subtitle: 'Control protocol',
    rarity: 'epic',
    unlockLevel: 3,
    unlockBlueprintId: null,
    color: 0x63efff,
    accentColor: 0xc33cff,
    abilityName: 'Absolute Zero',
    abilityDescription: 'Freezes the inner perimeter and fractures every trapped enemy.',
    abilityCooldown: 10.5,
    ability: { type: 'freeze', power: 34, radius: 178 },
    modifiers: {
      maxHealth: 1.04,
      damage: 0.98,
      attackSpeed: 0.96,
      armor: 1,
      critChance: 0,
      cooldownReduction: 0.08,
      area: 1.2,
    },
  },
  {
    id: 'singularity',
    name: 'Singularity Core',
    subtitle: 'Collapse protocol',
    rarity: 'legendary',
    unlockLevel: 5,
    unlockBlueprintId: 'gravity_matrix',
    color: 0xc33cff,
    accentColor: 0xffd321,
    abilityName: 'Event Horizon',
    abilityDescription: 'Pulls the battlefield inward before a catastrophic detonation.',
    abilityCooldown: 9.5,
    ability: { type: 'singularity', power: 52, radius: 230 },
    modifiers: {
      maxHealth: 0.88,
      damage: 1.13,
      attackSpeed: 1.05,
      armor: 0,
      critChance: 0.05,
      cooldownReduction: 0.12,
      area: 1.25,
    },
  },
];

export const ENEMIES: readonly EnemyDefinition[] = [
  {
    id: 'basic',
    name: 'Drone',
    health: 56,
    damage: 4,
    speed: 28,
    radius: 10,
    rewardCash: 3,
    rewardXP: 3,
    attackRange: 28,
    attackInterval: 1,
    color: 0xff265e,
    sides: 4,
  },
  {
    id: 'fast',
    name: 'Rusher',
    health: 38,
    damage: 3,
    speed: 48,
    radius: 8,
    rewardCash: 3,
    rewardXP: 3,
    attackRange: 25,
    attackInterval: 0.75,
    color: 0xffd321,
    sides: 4,
  },
  {
    id: 'tank',
    name: 'Bulwark',
    health: 195,
    damage: 9,
    speed: 17,
    radius: 15,
    rewardCash: 8,
    rewardXP: 7,
    attackRange: 33,
    attackInterval: 1.35,
    color: 0xff7a24,
    sides: 6,
  },
  {
    id: 'ranged',
    name: 'Raycaster',
    health: 88,
    damage: 6,
    speed: 24,
    radius: 11,
    rewardCash: 6,
    rewardXP: 5,
    attackRange: 150,
    attackInterval: 1.8,
    color: 0x00e5ff,
    sides: 3,
  },
  {
    id: 'protector',
    name: 'Protector',
    health: 150,
    damage: 4,
    speed: 21,
    radius: 13,
    rewardCash: 9,
    rewardXP: 8,
    attackRange: 30,
    attackInterval: 1.2,
    color: 0xc33cff,
    sides: 6,
  },
  {
    id: 'warden',
    name: 'The Warden',
    health: 1250,
    damage: 11,
    speed: 16,
    radius: 27,
    rewardCash: 90,
    rewardXP: 48,
    attackRange: 45,
    attackInterval: 1.2,
    color: 0xffd321,
    sides: 6,
  },
  {
    id: 'devourer',
    name: 'The Devourer',
    health: 5200,
    damage: 14,
    speed: 13,
    radius: 34,
    rewardCash: 200,
    rewardXP: 80,
    attackRange: 52,
    attackInterval: 1.1,
    color: 0xff3cff,
    sides: 8,
  },
];

export const WAVES: readonly WaveDefinition[] = [
  {
    wave: 1,
    duration: 45,
    spawnInterval: 1.35,
    enemies: [{ enemy: 'basic', weight: 100 }],
    elite: false,
    boss: false,
  },
  {
    wave: 2,
    duration: 45,
    spawnInterval: 1.2,
    enemies: [
      { enemy: 'basic', weight: 82 },
      { enemy: 'fast', weight: 18 },
    ],
    elite: false,
    boss: false,
  },
  {
    wave: 3,
    duration: 45,
    spawnInterval: 1.05,
    enemies: [
      { enemy: 'basic', weight: 62 },
      { enemy: 'fast', weight: 28 },
      { enemy: 'tank', weight: 10 },
    ],
    elite: false,
    boss: false,
  },
  {
    wave: 4,
    duration: 45,
    spawnInterval: 0.92,
    enemies: [
      { enemy: 'basic', weight: 54 },
      { enemy: 'fast', weight: 30 },
      { enemy: 'tank', weight: 16 },
    ],
    elite: false,
    boss: false,
  },
  {
    wave: 5,
    duration: 45,
    spawnInterval: 0.82,
    enemies: [
      { enemy: 'basic', weight: 45 },
      { enemy: 'fast', weight: 30 },
      { enemy: 'tank', weight: 25 },
    ],
    elite: true,
    boss: false,
    miniBoss: 'warden',
  },
  {
    wave: 6,
    duration: 45,
    spawnInterval: 0.74,
    enemies: [
      { enemy: 'basic', weight: 42 },
      { enemy: 'fast', weight: 25 },
      { enemy: 'tank', weight: 18 },
      { enemy: 'ranged', weight: 15 },
    ],
    elite: false,
    boss: false,
  },
  {
    wave: 7,
    duration: 45,
    spawnInterval: 0.66,
    enemies: [
      { enemy: 'basic', weight: 34 },
      { enemy: 'fast', weight: 26 },
      { enemy: 'tank', weight: 17 },
      { enemy: 'ranged', weight: 16 },
      { enemy: 'protector', weight: 7 },
    ],
    elite: false,
    boss: false,
  },
  {
    wave: 8,
    duration: 45,
    spawnInterval: 0.58,
    enemies: [
      { enemy: 'basic', weight: 30 },
      { enemy: 'fast', weight: 27 },
      { enemy: 'tank', weight: 18 },
      { enemy: 'ranged', weight: 16 },
      { enemy: 'protector', weight: 9 },
    ],
    elite: false,
    boss: false,
  },
  {
    wave: 9,
    duration: 45,
    spawnInterval: 0.5,
    enemies: [
      { enemy: 'basic', weight: 25 },
      { enemy: 'fast', weight: 28 },
      { enemy: 'tank', weight: 20 },
      { enemy: 'ranged', weight: 16 },
      { enemy: 'protector', weight: 11 },
    ],
    elite: false,
    boss: false,
  },
  { wave: 10, duration: 0, spawnInterval: 99, enemies: [], elite: false, boss: true },
];

export const UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'heavy_rounds',
    name: 'Heavy Rounds',
    description: '+22% kinetic damage and stronger knockback.',
    family: 'kinetic',
    rarity: 'common',
    tier: 1,
    maxRank: 3,
    requirements: [],
    tags: ['kinetic', 'impact'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'kineticDamage', valuePerRank: 0.22 }],
  },
  {
    id: 'piercing_ammo',
    name: 'Piercing Ammo',
    description: 'Projectiles pierce +1 target.',
    family: 'kinetic',
    rarity: 'uncommon',
    tier: 2,
    maxRank: 3,
    requirements: [{ upgradeId: 'heavy_rounds', minRank: 1 }],
    tags: ['kinetic', 'pierce'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'pierce', valuePerRank: 1 }],
  },
  {
    id: 'ricochet',
    name: 'Ricochet',
    description: 'Projectiles ricochet to +1 nearby target.',
    family: 'kinetic',
    rarity: 'rare',
    tier: 3,
    maxRank: 3,
    requirements: [
      { upgradeId: 'piercing_ammo', minRank: 1 },
      { upgradeId: 'gatling_mode', minRank: 1 },
    ],
    tags: ['kinetic', 'bounce'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'ricochet', valuePerRank: 1 }],
  },
  {
    id: 'explosive_shells',
    name: 'Explosive Shells',
    description: 'Impacts create an expanding blast.',
    family: 'kinetic',
    rarity: 'rare',
    tier: 3,
    maxRank: 3,
    requirements: [{ upgradeId: 'heavy_rounds', minRank: 2 }],
    tags: ['kinetic', 'area'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'explosionRadius', valuePerRank: 11 }],
  },
  {
    id: 'gatling_mode',
    name: 'Gatling Mode',
    description: '+14% kinetic attack speed.',
    family: 'kinetic',
    rarity: 'uncommon',
    tier: 1,
    maxRank: 3,
    requirements: [],
    tags: ['kinetic', 'speed'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'kineticAttackSpeed', valuePerRank: 0.14 }],
  },
  {
    id: 'tesla_arc',
    name: 'Tesla Arc',
    description: 'Install an auto-targeting chain-lightning emitter.',
    family: 'tesla',
    rarity: 'common',
    tier: 1,
    maxRank: 1,
    requirements: [],
    tags: ['tesla', 'electric'],
    blueprintId: null,
    effects: [{ type: 'unlock-weapon', weapon: 'tesla' }],
  },
  {
    id: 'chain_lightning',
    name: 'Chain Lightning',
    description: 'Tesla jumps to +1 additional target.',
    family: 'tesla',
    rarity: 'uncommon',
    tier: 2,
    maxRank: 3,
    requirements: [{ upgradeId: 'tesla_arc', minRank: 1 }],
    tags: ['tesla', 'chain'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'chainCount', valuePerRank: 1 }],
  },
  {
    id: 'static_charge',
    name: 'Static Charge',
    description: 'Repeated shocks stack bonus damage.',
    family: 'tesla',
    rarity: 'common',
    tier: 2,
    maxRank: 3,
    requirements: [{ upgradeId: 'tesla_arc', minRank: 1 }],
    tags: ['tesla', 'status'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'staticCharge', valuePerRank: 0.08 }],
  },
  {
    id: 'conductive_target',
    name: 'Conductive Target',
    description: 'Chains retain +12% more damage.',
    family: 'tesla',
    rarity: 'rare',
    tier: 3,
    maxRank: 3,
    requirements: [
      { upgradeId: 'chain_lightning', minRank: 1 },
      { upgradeId: 'static_charge', minRank: 1 },
    ],
    tags: ['tesla', 'chain'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'chainRetention', valuePerRank: 0.12 }],
  },
  {
    id: 'overcharge',
    name: 'Overcharge',
    description: 'Every fourth Tesla volley detonates an arc burst.',
    family: 'tesla',
    rarity: 'epic',
    tier: 4,
    maxRank: 3,
    requirements: [
      { upgradeId: 'chain_lightning', minRank: 2 },
      { upgradeId: 'static_charge', minRank: 2 },
    ],
    tags: ['tesla', 'burst'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'overcharge', valuePerRank: 0.2 }],
  },
  {
    id: 'cryo_emitter',
    name: 'Cryo Emitter',
    description: 'Install a radial pulse that slows nearby enemies.',
    family: 'cryo',
    rarity: 'common',
    tier: 1,
    maxRank: 1,
    requirements: [],
    tags: ['cryo', 'slow'],
    blueprintId: null,
    effects: [{ type: 'unlock-weapon', weapon: 'cryo' }],
  },
  {
    id: 'cryo_field',
    name: 'Cryo Field',
    description: '+18% pulse radius and persistent chill.',
    family: 'cryo',
    rarity: 'uncommon',
    tier: 2,
    maxRank: 3,
    requirements: [{ upgradeId: 'cryo_emitter', minRank: 1 }],
    tags: ['cryo', 'area'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'cryoRadius', valuePerRank: 0.18 }],
  },
  {
    id: 'frostbite',
    name: 'Frostbite',
    description: 'Chilled enemies take damage over time.',
    family: 'cryo',
    rarity: 'common',
    tier: 2,
    maxRank: 3,
    requirements: [{ upgradeId: 'cryo_emitter', minRank: 1 }],
    tags: ['cryo', 'damage-over-time'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'frostbite', valuePerRank: 0.16 }],
  },
  {
    id: 'deep_freeze',
    name: 'Deep Freeze',
    description: 'Fewer chill stacks are needed to freeze.',
    family: 'cryo',
    rarity: 'rare',
    tier: 3,
    maxRank: 3,
    requirements: [
      { upgradeId: 'cryo_field', minRank: 1 },
      { upgradeId: 'frostbite', minRank: 1 },
    ],
    tags: ['cryo', 'freeze'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'freezeThreshold', valuePerRank: 1 }],
  },
  {
    id: 'shatter',
    name: 'Shatter',
    description: 'Frozen enemies explode when destroyed.',
    family: 'cryo',
    rarity: 'epic',
    tier: 4,
    maxRank: 3,
    requirements: [
      { upgradeId: 'deep_freeze', minRank: 1 },
      { upgradeId: 'frostbite', minRank: 2 },
    ],
    tags: ['cryo', 'shatter'],
    blueprintId: null,
    effects: [{ type: 'weapon-rule', rule: 'shatter', valuePerRank: 0.28 }],
  },
  {
    id: 'gravity_well',
    name: 'Gravity Well',
    description: 'Periodic gravity pulses pull enemies inward.',
    family: 'utility',
    rarity: 'epic',
    tier: 3,
    maxRank: 3,
    requirements: [{ upgradeId: 'resonant_reactor', minRank: 1 }],
    tags: ['utility', 'gravity'],
    blueprintId: 'gravity_matrix',
    effects: [{ type: 'utility-rule', rule: 'gravity', valuePerRank: 0.2 }],
  },
  {
    id: 'critical_matrix',
    name: 'Critical Matrix',
    description: '+5% critical chance and +10% critical damage.',
    family: 'utility',
    rarity: 'uncommon',
    tier: 1,
    maxRank: 3,
    requirements: [],
    tags: ['utility', 'critical'],
    blueprintId: null,
    effects: [
      { type: 'core-add', stat: 'critChance', perRank: 0.05 },
      { type: 'core-add', stat: 'critMultiplier', perRank: 0.1 },
    ],
  },
  {
    id: 'resonant_reactor',
    name: 'Resonant Reactor',
    description: '+8% cooldown reduction and area size.',
    family: 'utility',
    rarity: 'rare',
    tier: 2,
    maxRank: 3,
    requirements: [{ upgradeId: 'critical_matrix', minRank: 1 }],
    tags: ['utility', 'reactor'],
    blueprintId: null,
    effects: [
      { type: 'core-add', stat: 'cooldownReduction', perRank: 0.08 },
      { type: 'core-multiplier', stat: 'area', perRank: 0.08 },
    ],
  },
  {
    id: 'repair_nanites',
    name: 'Repair Nanites',
    description: 'Regenerate 1.2 Core HP per second.',
    family: 'utility',
    rarity: 'uncommon',
    tier: 1,
    maxRank: 3,
    requirements: [],
    tags: ['utility', 'repair'],
    blueprintId: null,
    effects: [{ type: 'core-add', stat: 'regen', perRank: 1.2 }],
  },
  {
    id: 'salvage_protocol',
    name: 'Magnet Array',
    description: '+35% pickup radius and +10% XP and cash collected.',
    family: 'utility',
    rarity: 'rare',
    tier: 2,
    maxRank: 3,
    requirements: [{ upgradeId: 'repair_nanites', minRank: 1 }],
    tags: ['utility', 'economy', 'magnet'],
    blueprintId: null,
    effects: [
      { type: 'core-multiplier', stat: 'xp', perRank: 0.1 },
      { type: 'core-multiplier', stat: 'cash', perRank: 0.1 },
      { type: 'core-multiplier', stat: 'pickupRadius', perRank: 0.35 },
    ],
  },
];

export const SYNERGIES: readonly SynergyDefinition[] = [
  {
    id: 'shatter_rounds',
    name: 'Shatter Rounds',
    description: 'Kinetic impacts rupture frozen targets.',
    requirements: [
      { upgradeId: 'cryo_emitter', minRank: 1 },
      { upgradeId: 'shatter', minRank: 1 },
    ],
  },
  {
    id: 'superconductor',
    name: 'Superconductor',
    description: 'Tesla chains surge through chilled targets.',
    requirements: [
      { upgradeId: 'tesla_arc', minRank: 1 },
      { upgradeId: 'cryo_emitter', minRank: 1 },
      { upgradeId: 'conductive_target', minRank: 1 },
      { upgradeId: 'frostbite', minRank: 1 },
    ],
  },
  {
    id: 'thunderstorm',
    name: 'Thunderstorm',
    description: 'Gravity wells pulse with chain lightning.',
    requirements: [
      { upgradeId: 'overcharge', minRank: 1 },
      { upgradeId: 'gravity_well', minRank: 1 },
    ],
  },
  {
    id: 'implosion_rounds',
    name: 'Implosion Rounds',
    description: 'Explosions collapse inward before detonating.',
    requirements: [
      { upgradeId: 'explosive_shells', minRank: 1 },
      { upgradeId: 'gravity_well', minRank: 1 },
    ],
  },
  {
    id: 'cryo_vortex',
    name: 'Cryo Vortex',
    description: 'Gravity pulses become freezing vortexes.',
    requirements: [
      { upgradeId: 'cryo_field', minRank: 1 },
      { upgradeId: 'gravity_well', minRank: 1 },
    ],
  },
];

export const EVOLUTIONS: readonly EvolutionDefinition[] = [
  {
    id: 'shatter_cannon',
    name: 'SHATTER CANNON',
    description: 'Every heavy round fractures the battlefield.',
    synergyId: 'shatter_rounds',
    requirements: [
      { upgradeId: 'heavy_rounds', minRank: 3 },
      { upgradeId: 'deep_freeze', minRank: 3 },
    ],
  },
  {
    id: 'tesla_singularity',
    name: 'TESLA SINGULARITY',
    description: 'A permanent storm orbits the Core.',
    synergyId: 'thunderstorm',
    requirements: [
      { upgradeId: 'chain_lightning', minRank: 3 },
      { upgradeId: 'overcharge', minRank: 3 },
    ],
  },
];

export const SHOP_UPGRADES: readonly ShopDefinition[] = [
  { id: 'damage', name: 'Amplify Damage', description: '+10% all damage', baseCost: 30 },
  { id: 'attack-speed', name: 'Cycle Accelerators', description: '+8% attack speed', baseCost: 30 },
  { id: 'max-health', name: 'Reinforce Core', description: '+10% max HP and heal', baseCost: 28 },
  { id: 'armor', name: 'Reactive Armor', description: '+2 armor', baseCost: 25 },
  { id: 'regeneration', name: 'Repair Cycle', description: '+0.7 HP per second', baseCost: 26 },
  { id: 'critical', name: 'Targeting Matrix', description: '+3% critical chance', baseCost: 34 },
];

export const BLUEPRINTS = [
  {
    id: 'gravity_matrix',
    name: 'Gravity Matrix',
    description: 'Unlocks Gravity Well and its hybrid build paths.',
  },
] as const;

export function validateContent(): void {
  if (UPGRADES.length !== 20) throw new Error(`Expected 20 upgrades, found ${UPGRADES.length}.`);
  if (WAVES.length !== 10 || WAVES.at(-1)?.boss !== true)
    throw new Error('The run must contain 10 waves ending in a boss.');
  if (CORES.length < 4) throw new Error('The Core roster must contain four distinct playstyles.');

  const coreIds = new Set<string>();
  for (const core of CORES) {
    if (coreIds.has(core.id)) throw new Error(`Duplicate Core id: ${core.id}`);
    if (core.unlockLevel < 1 || core.abilityCooldown <= 0)
      throw new Error(`Invalid progression configuration for Core ${core.id}.`);
    coreIds.add(core.id);
  }

  const upgradeIds = new Set<string>();
  for (const upgrade of UPGRADES) {
    if (upgradeIds.has(upgrade.id)) throw new Error(`Duplicate upgrade id: ${upgrade.id}`);
    if (upgrade.maxRank < 1) throw new Error(`Upgrade ${upgrade.id} has no selectable ranks.`);
    upgradeIds.add(upgrade.id);
  }
  for (const upgrade of UPGRADES) {
    for (const requirement of upgrade.requirements) {
      if (!upgradeIds.has(requirement.upgradeId))
        throw new Error(`Unknown prerequisite ${requirement.upgradeId} in ${upgrade.id}.`);
      if (requirement.upgradeId === upgrade.id)
        throw new Error(`Upgrade ${upgrade.id} cannot require itself.`);
      const prerequisite = getUpgrade(requirement.upgradeId);
      if (prerequisite.tier >= upgrade.tier)
        throw new Error(`Upgrade ${upgrade.id} must depend on a lower-tier system.`);
    }
  }

  const synergyIds = new Set(SYNERGIES.map((synergy) => synergy.id));
  for (const synergy of SYNERGIES) {
    for (const requirement of synergy.requirements) {
      if (!upgradeIds.has(requirement.upgradeId))
        throw new Error(`Unknown upgrade ${requirement.upgradeId} in ${synergy.id}.`);
    }
  }
  for (const evolution of EVOLUTIONS) {
    if (!synergyIds.has(evolution.synergyId))
      throw new Error(`Unknown synergy ${evolution.synergyId} in ${evolution.id}.`);
    for (const requirement of evolution.requirements) {
      if (!upgradeIds.has(requirement.upgradeId))
        throw new Error(`Unknown upgrade ${requirement.upgradeId} in ${evolution.id}.`);
    }
  }
}

export function getUpgrade(id: string): UpgradeDefinition {
  const upgrade = UPGRADES.find((candidate) => candidate.id === id);
  if (!upgrade) throw new Error(`Unknown upgrade: ${id}`);
  return upgrade;
}

export function getEnemy(id: EnemyDefinition['id']): EnemyDefinition {
  const enemy = ENEMIES.find((candidate) => candidate.id === id);
  if (!enemy) throw new Error(`Unknown enemy: ${id}`);
  return enemy;
}

export function getCore(id: CoreDefinition['id']): CoreDefinition {
  const core = CORES.find((candidate) => candidate.id === id);
  if (!core) throw new Error(`Unknown Core: ${id}`);
  return core;
}
